import { useState } from "react";
import { Link } from "react-router";

import classes from "./SkinConsultation.module.css";
import { treatmentPages, servicesData } from "./serviceData";
import BookingWidgetSection from "../../Components/BookingWidget/BookingWidgetSection";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import {
  BOOKING_URL,
  trackBookingClick,
  trackFaqToggle,
} from "../../utils/tracking";

import heroImage from "../../Assets/portrait.jpeg";
import roomImage from "../../Assets/ra_room.png";

const page = treatmentPages.find((p) => p.slug === "skin-consultation");
const service = servicesData.skinConsultation.find(
  (s) => s.id === "skin-consultation",
);

const steps = [
  {
    title: "Book Your Free Slot",
    text: "Reserve a 20-minute consultation online — no cost, no obligation.",
  },
  {
    title: "Tell Us What's On Your Mind",
    text: "Talk through your skin concerns, questions or worries, at your own pace, with a qualified nurse.",
  },
  {
    title: "Leave With Clarity",
    text: "Walk away with honest advice on your options. Book a treatment only if and when you feel ready.",
  },
];

const faqs = [
  {
    q: "Is this consultation really free, with no catch?",
    a: "Yes — completely free for 20 minutes, and there's no obligation to book any treatment afterwards. Come along, have a chat, and decide in your own time.",
  },
  {
    q: "Will I be pressured into booking a treatment?",
    a: "Not at all. This consultation exists specifically for people who aren't ready to commit — you'll get honest, unbiased advice with zero pressure either way.",
  },
  {
    q: "I'm nervous about visiting a clinic for the first time — is that okay?",
    a: "Completely normal, and exactly who this consultation is for. There's no gown, no procedure and no pressure — just a relaxed conversation to help you feel comfortable before you decide on anything.",
  },
  {
    q: "What's the difference between this and the free Medik8 consultation?",
    a: "This consultation is a general chat about your skin concerns and treatment options. The Medik8 consultation is specifically focused on recommending an at-home skincare routine and products.",
  },
  {
    q: "Can I have a treatment on the day if I decide I'm ready?",
    a: "This particular appointment is a chat only, with no treatment included. If you already know you'd like a treatment carried out on the same day as your consultation, ask about our ",
    link: { to: "/treatments/advanced-facials", label: "Consultation & Treatment package" },
    tail: " instead.",
  },
];

const SkinConsultationPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Skin Consultation FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>
              Nurse-Led · Free · No Obligation
            </p>
            <h1 className={classes.heroTitle}>Free Skin Consultation</h1>
            <p className={classes.heroMeta}>
              <span>Free</span> · {service.duration}
            </p>
            <p className={classes.heroDescription}>
              Not ready to book a treatment, or simply not sure where to
              start? Come in for a relaxed, no-obligation chat about your
              skin — no professional treatment on the day, no pressure, and
              no commitment required. Just an honest conversation to help you
              feel informed and at ease.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Skin Consultation Hero")}
            >
              Book Your Free Consultation
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Friendly nurse at Radiance Aesthetics ready to welcome you for a free skin consultation"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>How It Works</p>
          <h2 className={classes.sectionTitle}>
            Your Consultation, Step By Step
          </h2>
          <p className={classes.sectionDescription}>
            A simple, no-pressure conversation about your skin.
          </p>
          <div className={classes.stepGrid}>
            {steps.map((step, i) => (
              <div className={classes.stepCard} key={step.title}>
                <div className={classes.stepNumber}>{i + 1}</div>
                <h3 className={classes.stepTitle}>{step.title}</h3>
                <p className={classes.stepText}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why book with us */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <div className={classes.whyRow}>
            <img
              className={classes.whyImage}
              src={roomImage}
              alt="Calm, private treatment room at Radiance Aesthetics"
              loading="lazy"
            />
            <div className={classes.whyContent}>
              <p className={classes.sectionLabel} style={{ textAlign: "left" }}>
                Feeling Nervous? That's Completely Normal
              </p>
              <p className={classes.whyText}>
                Booking a clinic appointment for the first time can feel
                daunting, especially if you're unsure what to expect or
                worried about being talked into something you don't want.
                This consultation exists to take that pressure away
                entirely.
              </p>
              <div>
                <span className={classes.whyBadge}>
                  100% Free · Zero Obligation · No Judgement
                </span>
              </div>
              <p className={classes.whyText}>
                You'll spend your 20 minutes in a calm, private room, talking
                openly with a qualified nurse about your skin — no scripts,
                no sales pitch, just honest, personalised advice.
              </p>
              <Link to="/reviews" className={classes.whyLink}>
                See what our clients say →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className={`${classes.section} ${classes.sectionAlt}`}
        ref={faqSectionRef}
      >
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>
                    {faq.a}
                    {faq.link && (
                      <Link to={faq.link.to}>{faq.link.label}</Link>
                    )}
                    {faq.tail}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <h2 className={classes.ctaTitle}>Ready to Talk It Through?</h2>
        <p className={classes.ctaDescription}>
          Book your free 20-minute skin consultation today — no cost, no
          obligation, no pressure.
        </p>
        <a
          href={BOOKING_URL}
          className={classes.ctaButton}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackBookingClick("Skin Consultation Bottom CTA")}
        >
          Book Your Free Consultation
        </a>
      </section>

      <div className={classes.ctaWidgetWrapper}>
        <BookingWidgetSection source="Treatments - Skin Consultation" />
      </div>
    </>
  );
};

export default SkinConsultationPage;
